import React, { useCallback, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  FlexList,
  Heading,
  Inscription,
  PaginationBar,
  Preloader,
  SearchInput,
  SelectDropdown,
} from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { ListItemBase, TableRowOption } from '@/common/types';
import HeaderLink from '@/components/elements/HeaderLink';
import useHistory from '@/modules/core/hooks/useHistory';

import ListPlaceholder from '../ListPlaceholder';

import { CreatePayoutDefinitionModalContainer } from './CreatePayoutDefinitionModal';
import { usePayoutDefinitions, useSearchParams } from './hooks';

const headers: ListItemBase[] = [
  {
    key: 'Name',
    title: 'Name',
    flex: 2,
  },
  {
    key: 'Enabled service categories',
    title: 'Enabled service categories',
    flex: 3,
  },
  {
    key: 'Is limited?',
    title: 'Is limited?',
    flex: 1,
  },
  {
    key: 'Organization type',
    title: 'Organization type',
    flex: 1,
  },
];

interface Props {
  isPageMode?: boolean;
}

const PayoutDefinitions: React.FC<Props> = ({ isPageMode }) => {
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const history = useHistory();
  const {
    page,
    setPage,
    setPerPage,
    perPage,
    searchString,
    setSearchString,
    limitedFilterOptions,
    limitedFilter,
    setLimitedFilter,
    isLimitedUse,
    params,
  } = useSearchParams();
  const { formatedList, isLoading, total } = usePayoutDefinitions(params, isLimitedUse);

  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalVisible(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalVisible(false);
  }, []);

  const options = useMemo<TableRowOption[]>(
    () => [
      {
        name: 'Edit',
        onClick: (id: string) => {
          history.push(ROUTES.PAYOUT_DEFINITION_EDIT_BY_ID(id));
        },
      },
    ],
    [history]
  );

  const currentNode = useMemo(() => {
    if (isLoading) {
      return (
        <Box pad={{ top: 'spacing24' }}>
          <Preloader testId="payout-definition_loading" />
        </Box>
      );
    }
    if (formatedList.length === 0) {
      return <ListPlaceholder testId="payout-definition">There are no Payout Definitions</ListPlaceholder>;
    }
    return (
      <Box direction="column" style={{ position: 'relative' }}>
        {total === 0 ? null : (
          <PaginationBar
            page={page}
            total={total}
            pageSize={perPage}
            onChangePage={setPage}
            onChangePageSize={setPerPage}
          />
        )}
      </Box>
    );
  }, [formatedList, isLoading, page, perPage, setPage, setPerPage, total]);
  return (
    <div>
      <Box
        direction="column"
        data-testid="payout-definition"
        pad={{ bottom: 'spacing24' }}
        border={!isPageMode && { side: 'bottom', size: 'small', color: 'border1' }}
      >
        <Box direction="row" align="center" justify="between">
          {isPageMode ? (
            <Box pad={{ vertical: 'l' }}>
              <Heading level={4} size="large" color="textTitle">
                Payout Definitions
              </Heading>
            </Box>
          ) : (
            <Box margin={{ top: 'l', bottom: 'm' }}>
              <HeaderLink path={ROUTES.PAYOUT_DEFINITIONS} title="Payout Definitions" />
            </Box>
          )}
          <AppButton testId="payout-definition_create-button" onClick={handleOpenCreateModal}>
            Create Payout Definition
          </AppButton>
        </Box>
        <Box>
          <FlexList
            testId="payout-definition-list"
            headers={headers}
            rows={formatedList}
            options={options}
            footer={currentNode}
          >
            <Box direction="row" justify="between" align="center" pad={{ vertical: 'spacing12' }}>
              <Inscription weight="bold" color="textBody" lineHeight="20px">
                Payout Definitions: {total || 0}
              </Inscription>
              <Box direction="row">
                <SearchInput
                  hasSearchingHistory
                  className="search-input"
                  testId="payout-definition-search"
                  value={searchString}
                  placeholder="Search"
                  onChange={setSearchString}
                />
                <Box direction="row" margin={{ left: 'spacing8' }}>
                  <Box width="small">
                    <SelectDropdown
                      id="limitedFilter"
                      singleMode
                      testId="payout-definition-limited-filter"
                      ellipsisMode
                      activeTitle
                      name="Limited"
                      prefix="Limited"
                      options={limitedFilterOptions}
                      value={limitedFilter}
                      onChange={setLimitedFilter}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </FlexList>
        </Box>
      </Box>
      <CreatePayoutDefinitionModalContainer visible={isCreateModalVisible} onClose={handleCloseCreateModal} />
    </div>
  );
};

export default PayoutDefinitions;
